import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { EmailLoginForm } from './providers/EmailLoginForm';
import { PricingCards } from '../billing/PricingCards';
import { ROUTES } from '../../config/routes';
import { SpotifyIcon, QrIcon, SpeakerIcon } from '../../shared/components/icons';

// Grano de película sutil sobre el fondo (mismo que RoomView).
const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const STEPS = [
  {
    Icon: SpotifyIcon,
    title: 'Conecta tu cuenta de Spotify Premium',
    text: 'Envía el correo de tu cuenta, la verificamos y la conectas desde tu panel.',
  },
  {
    Icon: QrIcon,
    title: 'Imprime tu QR',
    text: 'Descarga tu QR principal, imprímelo y listo. Sin complicaciones.',
  },
  {
    Icon: SpeakerIcon,
    title: 'Tus clientes eligen, suena en tu local',
    text: 'Escanean desde el celular, sin descargar nada ni registrarse.',
  },
];

function Wordmark({ className = '' }) {
  return (
    <span className={`font-inter font-semibold uppercase tracking-[0.32em] text-lounge-text ${className}`}>
      Rockcola
    </span>
  );
}

function Eyebrow({ children }) {
  return (
    <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-lounge-brass">
      {children}
    </span>
  );
}

export function LoginPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate(ROUTES.CONTROL, { replace: true });
  }, [user, navigate]);

  return (
    <div className="relative min-h-dvh overflow-x-hidden bg-lounge-base font-inter text-lounge-text antialiased
                    selection:bg-lounge-brass/30">
      <div aria-hidden className="pointer-events-none fixed inset-0 opacity-[0.035]" style={{ backgroundImage: GRAIN }} />

      <header className="sticky top-0 z-40 border-b border-lounge-line bg-lounge-base/75
                         pt-[env(safe-area-inset-top)] backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 lg:px-12">
          <Wordmark className="text-[13px]" />
          <nav className="flex items-center gap-2">
            <a
              href="#planes"
              className="px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-lounge-stone
                         transition-colors hover:text-lounge-text"
            >
              Planes
            </a>
            <a
              href="#login"
              className="rounded-full border border-lounge-brass/40 px-4 py-2 font-mono text-[11px] uppercase
                         tracking-[0.15em] text-lounge-brass transition-colors hover:bg-lounge-brass/10"
            >
              Ingresar
            </a>
          </nav>
        </div>
      </header>

      <main className="relative">
        {/* Hero + acceso */}
        <section className="relative mx-auto max-w-6xl px-5 py-12 lg:px-12 lg:py-24">
          <div
            aria-hidden
            className="pointer-events-none absolute -top-24 left-1/4 h-[420px] w-[420px] rounded-full
                       bg-lounge-brass/[0.07] blur-[120px]"
          />
          <div className="relative grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7 lg:pr-8">
              <h1 className="font-serif text-[40px] font-semibold leading-[1.08] tracking-tight text-lounge-text
                             sm:text-5xl lg:text-[64px]">
                Tu selección,<br />
                <span className="italic text-lounge-brass">en vivo.</span>
              </h1>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-lounge-stone lg:text-lg">
                Tus clientes eligen la siguiente canción,{' '}
                <span className="font-serif italic text-lounge-text">tú mantienes siempre el control.</span>
              </p>
            </div>

            <div id="login" className="scroll-mt-24 lg:col-span-5">
              <div className="rounded-lg border border-lounge-line bg-lounge-card p-6 shadow-sleeve sm:p-8">
                <Eyebrow>Acceso al panel</Eyebrow>
                <h2 className="mb-6 font-serif text-2xl font-medium text-lounge-text">Ingresar a RockCola</h2>
                <EmailLoginForm />
                <p className="mt-6 text-center text-xs leading-relaxed text-lounge-stone">
                  Al continuar, aceptas nuestros{' '}
                  <a href="/terms" className="text-lounge-stone underline underline-offset-2 hover:text-lounge-text">
                    Términos de Servicio
                  </a>
                  {' '}y{' '}
                  <a href="/privacy" className="text-lounge-stone underline underline-offset-2 hover:text-lounge-text">
                    Política de Privacidad
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Cómo funciona */}
        <section className="mx-auto max-w-6xl px-5 py-12 lg:px-12">
          <div className="mb-10 text-center">
            <h2 className="font-serif text-3xl font-medium text-lounge-text lg:text-4xl">Cómo funciona</h2>
          </div>
          <ol className="relative grid gap-4 md:grid-cols-3">
            <li
              aria-hidden
              className="pointer-events-none absolute left-[15%] right-[15%] top-[52px] hidden border-t
                         border-dashed border-lounge-brass/30 md:block"
            />
            {STEPS.map(({ Icon, title, text }, i) => (
              <li key={title} className="relative rounded-lg border border-lounge-line bg-lounge-card p-6">
                <div className="flex items-center justify-between">
                  <span className="flex h-14 w-14 items-center justify-center rounded-lg border border-lounge-brass/30
                                   bg-lounge-raised text-lounge-brass shadow-[0_0_15px_rgba(229,169,60,0.12)]">
                    <Icon className="h-7 w-7" strokeWidth={1.5} />
                  </span>
                  <span className="font-mono text-xs tracking-wider text-lounge-brass">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="mt-4 text-lg font-semibold text-lounge-text">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-lounge-stone">{text}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Planes */}
        <section id="planes" className="mx-auto max-w-6xl scroll-mt-16 px-5 py-12 lg:px-12 lg:pb-24">
          <div className="mb-8 text-center">
            <Eyebrow>Suscripción</Eyebrow>
            <h2 className="font-serif text-3xl font-medium text-lounge-text lg:text-4xl">Planes para tu local</h2>
          </div>
          <PricingCards />
          <p className="mt-6 text-center text-sm text-lounge-stone">La activación se coordina por WhatsApp.</p>
        </section>
      </main>

      <footer className="relative border-t border-lounge-line">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 px-5 py-8 text-xs text-lounge-stone
                        sm:flex-row sm:justify-between lg:px-12">
          <Wordmark className="text-[11px]" />
          <div className="flex gap-5">
            <a href="/terms" className="transition-colors hover:text-lounge-text">Términos</a>
            <a href="/privacy" className="transition-colors hover:text-lounge-text">Privacidad</a>
          </div>
          <span>© RockCola</span>
        </div>
      </footer>
    </div>
  );
}
